import { useCallback } from 'react';
import { toast as sonnerToast } from 'sonner';

export interface ToastOptions {
  description?: string;
  duration?: number;
  id?: string | number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function useToast() {
  const toastSuccess = useCallback((message: string, options?: ToastOptions) => {
    return sonnerToast.success(message, {
      description: options?.description,
      duration: options?.duration,
      id: options?.id,
      action: options?.action,
    });
  }, []);

  const toastError = useCallback((message: string, options?: ToastOptions) => {
    return sonnerToast.error(message, {
      description: options?.description,
      duration: options?.duration,
      id: options?.id,
      action: options?.action,
    });
  }, []);

  const toastInfo = useCallback((message: string, options?: ToastOptions) => {
    return sonnerToast.info(message, {
      description: options?.description,
      duration: options?.duration,
      id: options?.id,
      action: options?.action,
    });
  }, []);

  const toastLoading = useCallback((message: string, options?: ToastOptions) => {
    return sonnerToast.loading(message, {
      description: options?.description,
      duration: options?.duration,
      id: options?.id,
    });
  }, []);

  const toastPromise = useCallback(
    <T>(
      promise: Promise<T>,
      messages: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((err: unknown) => string);
      }
    ) => {
      return sonnerToast.promise(promise, messages);
    },
    []
  );

  const dismissToast = useCallback((id?: string | number) => {
    sonnerToast.dismiss(id);
  }, []);

  return {
    toastSuccess,
    toastError,
    toastInfo,
    toastLoading,
    toastPromise,
    dismissToast,
    toast: sonnerToast,
  };
}
